/**
 * Authentication API
 * Login and Register endpoints
 */

import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Base URL from main api.js
const BASE_URL = 'https://a77.onetradebot.com/api';

// Global flag to prevent multiple simultaneous logouts
let isLoggingOut = false;

// Create axios instance for auth
const authClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response Interceptor
authClient.interceptors.response.use(
  (response) => {
    console.log(`Auth API Response: ${response.status} ${response.config?.method?.toUpperCase()} ${response.config?.url}`);
    console.log('Response data:', response.data);
    return response.data;
  },
  (error) => {
    console.error('Auth API Error Details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data
    });
    return Promise.reject(error);
  }
);

// ==================== Authentication APIs ====================
export const authAPI = {

  // Register new user
  register: async (userData) => {
    try {
      console.log('🔵 REGISTER API CALL START');
      console.log('📍 Full URL:', BASE_URL + '/auth/register');
      console.log('📤 Method: POST');
      console.log('📋 Headers: Content-Type: application/json');
      console.log('📦 Payload:', { ...userData, password: '[HIDDEN]' });
      console.log('⏱️ Timeout: 15000ms');
      console.log('=====================================');
      
      const response = await authClient.post('/auth/register', userData);
      
      console.log('✅ REGISTER API SUCCESS');
      console.log('📥 Response Status:', response.status || 'Success');
      console.log('📄 Response Data:', response);
      console.log('🔑 Token in response:', !!response?.token);
      console.log('👤 User in response:', !!response?.user);
      
      return response;
    } catch (error) {
      console.error('❌ REGISTER API FAILED');
      console.error('🚨 Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message
      });
      throw error;
    }
  },
  
  // Login user
  login: async (credentials) => {
    try {
      console.log('🔐 LOGIN API CALL');
      console.log('📍 Full URL:', `${BASE_URL}/auth/login`);
      console.log('📤 Method: POST');
      console.log('📋 Headers: Content-Type: application/json');
      console.log('📦 Payload:', { ...credentials, password: '[HIDDEN]' });
      console.log('⏱️ Timeout: 15000ms');
      console.log('=====================================');
      
      const response = await authClient.post('/auth/login', credentials);
      
      console.log('✅ LOGIN SUCCESS');
      console.log('📥 Response Status:', response.status || 'Success');
      console.log('📄 Response Data:', response);
      
      return response;
    } catch (error) {
      console.log('❌ LOGIN FAILED');
      console.log('🚨 Error Details:', {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  },

  // Send OTP to email for verification
  sendOTP: async (email) => {
    try {
      console.log('📧 SEND OTP API CALL START');
      console.log('📍 Full URL:', BASE_URL + '/auth/send-otp');
      console.log('📤 Method: POST');
      console.log('📦 Payload:', { email });
      console.log('=====================================');
      
      const response = await authClient.post('/auth/send-otp', { email });
      
      console.log('✅ SEND OTP API SUCCESS');
      console.log('📥 Response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ SEND OTP API FAILED');
      console.error('🚨 Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message
      });
      throw error;
    }
  },

  // Verify OTP - Updated to match your backend
  verifyOTP: async (email, otp) => {
    try {
      console.log('🔐 VERIFY OTP API CALL START');
      console.log('📍 Full URL:', BASE_URL + '/auth/verify-otp');
      console.log('📤 Method: POST');
      console.log('📦 Payload:', { email, otp: '[HIDDEN]' });
      console.log('=====================================');
      
      // Using your exact API format
      const response = await authClient.post('/auth/verify-otp', { 
        email: email,
        otp: otp 
      });
      
      console.log('✅ VERIFY OTP API SUCCESS');
      console.log('📥 Response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ VERIFY OTP API FAILED');
      console.error('🚨 Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message
      });
      throw error;
    }
  },

  // Forgot Password - Send OTP to email
  forgotPassword: async (email) => {
    try {
      console.log('🔐 FORGOT PASSWORD API CALL START');
      console.log('📍 Full URL:', BASE_URL + '/auth/forgot-password');
      console.log('📤 Method: POST');
      console.log('📦 Payload:', { email });
      console.log('=====================================');
      
      const response = await authClient.post('/auth/forgot-password', { email });
      
      console.log('✅ FORGOT PASSWORD API SUCCESS');
      console.log('📥 Response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ FORGOT PASSWORD API FAILED');
      console.error('🚨 Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message
      });
      throw error;
    }
  },

  // Reset Password with OTP
  resetPassword: async (email, otp, newPassword) => {
    try {
      console.log('🔒 RESET PASSWORD API CALL START');
      console.log('📍 Full URL:', BASE_URL + '/auth/reset-password');
      console.log('📤 Method: POST');
      console.log('📦 Payload:', { email, otp: '[HIDDEN]', newPassword: '[HIDDEN]' });
      console.log('=====================================');
      
      const response = await authClient.post('/auth/reset-password', { 
        email, 
        otp, 
        newPassword 
      });
      
      console.log('✅ RESET PASSWORD API SUCCESS');
      console.log('📥 Response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ RESET PASSWORD API FAILED');
      console.error('🚨 Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message
      });
      throw error;
    }
  },
  
  // Get user profile
  getProfile: async () => {
    try {
      const token = await AsyncStorage.getItem('authToken');
      const response = await authClient.get('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  // Update user profile
  updateProfile: async (profileData) => {
    try {
      console.log('🔵 UPDATE PROFILE API CALL START');
      const token = await AsyncStorage.getItem('authToken');
      console.log('🔑 Token:', token ? 'Present' : 'Missing');
      
      // Check if profileData is FormData (for image upload)
      const isFormData = profileData instanceof FormData;
      console.log('📦 Data type:', isFormData ? 'FormData (with image)' : 'JSON');
      
      console.log('📍 URL: https://a77.onetradebot.com/api/auth/profile');
      console.log('📤 Method: PATCH');
      console.log('📋 Content-Type:', isFormData ? 'multipart/form-data' : 'application/json');
      
      const response = await axios.patch('https://a77.onetradebot.com/api/auth/profile', profileData, {
        headers: {
          'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('✅ UPDATE PROFILE SUCCESS');
      console.log('📥 Response status:', response.status);
      console.log('📄 Response data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ UPDATE PROFILE FAILED');
      console.error('🚨 Error status:', error.response?.status);
      console.error('🚨 Error message:', error.response?.data?.message || error.message);
      console.error('🚨 Full error:', error.response?.data);
      throw error;
    }
  },
  
  // Change password
  changePassword: async (passwordData) => {
    try {
      const token = await AsyncStorage.getItem('authToken');
      // Use PATCH method instead of PUT
      const response = await axios.patch('https://a77.onetradebot.com/api/auth/profile', {
        password: passwordData.newPassword,
      }, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Store token
  saveToken: async (token) => {
    try {
      await AsyncStorage.setItem('authToken', token);
      console.log('Token saved successfully');
    } catch (error) {
      console.error('Error saving token:', error);
      throw error;
    }
  },
  
  // Get token
  getToken: async () => {
    try {
      const token = await AsyncStorage.getItem('authToken');
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  },
  
  // Remove token (logout)
  logout: async () => {
    // Prevent multiple simultaneous logouts
    if (isLoggingOut) {
      console.log('Logout already in progress...');
      return;
    }
    
    try {
      isLoggingOut = true;
      await AsyncStorage.removeItem('authToken');
      console.log('Token removed successfully');
    } catch (error) {
      console.error('Error removing token:', error);
      throw error;
    } finally {
      isLoggingOut = false;
    }
  },
  
  // Save selected server
  saveSelectedServer: async (server) => {
    try {
      await AsyncStorage.setItem('selected_server', server);
      console.log('Server saved successfully:', server);
    } catch (error) {
      console.error('Error saving server:', error);
      throw error;
    }
  },
  
  // Get selected server
  getSelectedServer: async () => {
    try {
      const server = await AsyncStorage.getItem('selected_server');
      return server || 'fizmoFXMarket-Server';
    } catch (error) {
      console.error('Error getting server:', error);
      return 'fizmoFXMarket-Server';
    }
  },
};

export default authAPI;
